import * as analytic from '@anm/analytic';
import TabUrlParameters, { FormValues } from '@anm/components/video-landings/TabUrlParameters';
import { DEFAULT_WAVE_COLOR } from '@anm/constants/colors';
import { emailCampaignFields } from '@anm/data/video-landings/form';
import downloadFile from '@anm/helpers/downloadFile';
import { toUrlParams } from '@anm/helpers/parseParams';
import isEqual from 'lodash/fp/isEqual';
import omitBy from 'lodash/fp/omitBy';
import React, { useCallback, useEffect, useState, ChangeEvent, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../../../config';
import { hostedVideoSelectors } from '../../../../../store/modules/hostedVideo';
import { landingsActions, landingsSelectors } from '../../../../../store/modules/landings';
import SizesWithButton from '../../SizesWithButton';
import useThumbnailSizes from '../../hooks/useThumbnailSizes';
import { TabTitle } from '../../index';

import {
  DownloadlThumbnail,
  LinkInput,
  LinkWrapper,
  LoaderWrapper,
  Subtitle,
  ThumbnailWrapper,
  Wrapper
} from './Wrapper';
import { generateSnippet, getPreviewFullSrc } from './helpers';
import { EmailTabContentProps, EmailTabProps } from './types';
import useEmailThumbPreview from './hooks/useEmailThumbPreview';
import useIsWebinar from '../../../../../hooks/useIsWebinar';

const config = vtConfig();

const CAMPAIGN_KEY = 'emailCampaign';

const EmailTabContent: FC<EmailTabContentProps> = ({ color, hostId, videoName, videoSizes, landingParams }) => {
  const isWebinar = useIsWebinar();
  const defaultLink = isWebinar ? `${config.urls.webinarUrl}${hostId}` : `${config.urls.videoLanding}${hostId}`;
  const previewSrc = useEmailThumbPreview({ hostId, videoSizes });

  const { playButton, link: linkFromParams, ...initialValues } = landingParams[CAMPAIGN_KEY] || {};

  const initialPlayButtonState = playButton !== undefined ? playButton : true;
  const urlParams = toUrlParams(initialValues as FormValues);

  const videoSources = useSelector(hostedVideoSelectors.selectVideoSources);
  const isDownloadButtonDisabled = !videoSources?.length;

  const [playButtonStatus, setPlayButonStatus] = useState(initialPlayButtonState);
  const [link, setLink] = useState(linkFromParams || defaultLink);
  const [formCurrentValues, setFormCurrentValues] = useState(initialValues);

  const [sizes, setSizes] = useThumbnailSizes(videoSizes);

  useEffect(() => {
    analytic.trackOpenEmailCampaign();
  }, []);

  const dispatch = useDispatch();

  const previewFullSrc = getPreviewFullSrc({
    sizes,
    link: previewSrc,
    playButton: { color, enabled: playButtonStatus }
  });

  const handleCheckboxChange = useCallback(() => {
    const newButtonStatus = !playButtonStatus;

    setPlayButonStatus(newButtonStatus);
    dispatch(
      landingsActions.updateLanding({
        parameters: {
          ...landingParams,
          [CAMPAIGN_KEY]: {
            ...initialValues,
            link,
            playButton: newButtonStatus
          }
        }
      })
    );
  }, [formCurrentValues, playButtonStatus, link]);

  const handleInputChange = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => setLink(value), []);

  const handleInputBlur = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      !value && setLink(defaultLink);

      dispatch(
        landingsActions.updateLanding({
          parameters: {
            ...landingParams,
            [CAMPAIGN_KEY]: {
              ...landingParams[CAMPAIGN_KEY],
              link: value || defaultLink
            }
          }
        })
      );
    },
    [landingParams]
  );

  const handleFormBlur = useCallback(
    (values: FormValues) => {
      setFormCurrentValues(values);
      analytic.trackAddURLParams(values);

      const oldValues = landingParams?.[CAMPAIGN_KEY];
      const oldValuesWithoutPlayButton = omitBy((_: string, key: string) => key === 'playButton')(oldValues);

      if (isEqual(values, oldValuesWithoutPlayButton)) return;

      dispatch(
        landingsActions.updateLanding({
          parameters: {
            ...landingParams,
            [CAMPAIGN_KEY]: { ...values, playButton: playButtonStatus }
          }
        })
      );
    },
    [landingParams, playButtonStatus]
  );

  const handleDownloadThumbnail = useCallback(() => {
    analytic.trackDownloadThumbnail();
    downloadFile({
      url: previewFullSrc,
      type: 'image/jpg',
      fileName: `${videoName?.replace(/\s/g, '_')}.jpg`
    });
  }, [sizes, playButtonStatus]);

  const snippet = generateSnippet({
    link,
    sizes,
    urlParams,
    alt: videoName,
    src: previewSrc,
    playButton: {
      color,
      enabled: playButtonStatus
    }
  });

  const handleCopy = useCallback(() => analytic.trackCopyEmailCampaignCode(), []);

  return (
    <Wrapper>
      <TabTitle title="Email campaign" />
      <ThumbnailWrapper>
        <SizesWithButton {...{ sizes, setSizes, playButtonStatus, handleCheckboxChange }} />
        <DownloadlThumbnail
          size="medium_small"
          variant="silver"
          buttonType="container"
          onClick={handleDownloadThumbnail}
          disabled={isDownloadButtonDisabled}
        >
          Download
        </DownloadlThumbnail>
      </ThumbnailWrapper>

      <TabUrlParameters
        snippet={snippet}
        title="Email video tag"
        fields={emailCampaignFields}
        formInitialValues={initialValues as FormValues}
        onCopyCode={handleCopy}
        onFormBlur={handleFormBlur}
      >
        <LinkWrapper>
          <Subtitle title="Links to" level={4} />
          <LinkInput value={link} onBlur={handleInputBlur} onChange={handleInputChange} />
        </LinkWrapper>
      </TabUrlParameters>
    </Wrapper>
  );
};

const EmailTab: FC<EmailTabProps> = props => {
  const landing = useSelector(landingsSelectors.selectLanding);
  const color = useSelector(hostedVideoSelectors.selectPlayerColor) || DEFAULT_WAVE_COLOR;

  if (!landing?.parameters) {
    return <LoaderWrapper size="large" color="blue" />;
  }

  const { parameters: landingParams } = landing;

  return <EmailTabContent {...{ ...props, color, landingParams }} />;
};

export default EmailTab;
